import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Check, ArrowLeft, Zap, TrendingUp } from 'lucide-react';

const Pricing = () => {
  const navigate = useNavigate();

  const plans = [
    {
      name: 'Pay Per Test',
      price: '$49.99',
      period: 'per test',
      description: 'Perfect for validating individual product concepts',
      features: [
        'Single product concept test',
        'Up to 100 AI consumer simulations',
        'Full demographic breakdown',
        'Purchase intent analysis',
        'Market segment insights',
        'Detailed response samples',
        'Export results to PDF',
        '48-hour access to results'
      ],
      cta: 'Buy Single Test',
      popular: false,
      icon: Zap
    },
    {
      name: 'Unlimited Monthly',
      price: '$499.99',
      period: 'per month',
      description: 'For teams testing multiple products continuously',
      features: [
        'Unlimited product tests',
        'Up to 500 AI consumers per test',
        'Priority processing',
        'All Pay Per Test features',
        'Advanced analytics dashboard',
        'Trend analysis across products',
        'Team collaboration (up to 5 users)',
        'API access',
        'Dedicated support',
        'Custom demographic targeting'
      ],
      cta: 'Start Monthly Plan',
      popular: true,
      icon: TrendingUp
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Notice Banner */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white py-4">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-lg font-semibold">
            ⚠️ Example Pricing Only - Platform is currently invite-only
          </p>
          <p className="text-sm text-amber-50 mt-1">
            These prices are for demonstration purposes. Access is currently available via invite code only.
          </p>
        </div>
      </div>

      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-slate-600 hover:text-indigo-600"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </div>
      </div>

      {/* Pricing Header */}
      <div className="max-w-7xl mx-auto px-6 py-16 text-center">
        <h1 className="text-5xl font-bold text-slate-900 mb-4">
          Simple, Transparent Pricing
        </h1>
        <p className="text-xl text-slate-600 max-w-2xl mx-auto">
          Get AI-powered consumer insights in minutes. No hidden fees, no surprises.
        </p>
      </div>

      {/* Pricing Cards */}
      <div className="max-w-7xl mx-auto px-6 pb-20">
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {plans.map((plan) => (
            <Card
              key={plan.name}
              className={`relative p-8 ${
                plan.popular
                  ? 'border-2 border-indigo-500 shadow-2xl scale-105'
                  : 'border border-slate-200 shadow-lg'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-indigo-600 to-cyan-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                    Most Popular
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-indigo-100 mb-4">
                  <plan.icon className="w-8 h-8 text-indigo-600" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">
                  {plan.name}
                </h3>
                <p className="text-slate-600 mb-4">{plan.description}</p>
                <div className="flex items-baseline justify-center gap-2">
                  <span className="text-5xl font-bold text-slate-900">
                    {plan.price}
                  </span>
                  <span className="text-slate-600">/ {plan.period}</span>
                </div>
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => navigate('/')}
                className={`w-full py-6 text-lg font-semibold ${
                  plan.popular
                    ? 'bg-gradient-to-r from-indigo-600 to-cyan-600 hover:from-indigo-700 hover:to-cyan-700 text-white'
                    : 'bg-slate-900 hover:bg-slate-800 text-white'
                }`}
              >
                Request Invite Code
              </Button>
              
              <p className="text-xs text-slate-500 text-center mt-3">
                Currently invite-only. Click above to request access.
              </p>
            </Card>
          ))}
        </div>

        {/* FAQ Section */}
        <div className="mt-20 max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-slate-900 mb-12">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-6">
            <Card className="p-6">
              <h3 className="font-semibold text-lg text-slate-900 mb-2">
                How accurate are the AI simulations?
              </h3>
              <p className="text-slate-600">
                Our AI consumer simulations achieve 85-90% correlation with real human purchase intent data, 
                based on academic research using GPT-4o and semantic similarity rating methods.
              </p>
            </Card>

            <Card className="p-6">
              <h3 className="font-semibold text-lg text-slate-900 mb-2">
                How long does a test take?
              </h3>
              <p className="text-slate-600">
                Most tests complete in 2-5 minutes. You'll receive comprehensive results including purchase intent scores, 
                demographic breakdowns, and detailed consumer responses immediately after completion.
              </p>
            </Card>

            <Card className="p-6">
              <h3 className="font-semibold text-lg text-slate-900 mb-2">
                Can I cancel my monthly subscription anytime?
              </h3>
              <p className="text-slate-600">
                Yes! You can cancel your Unlimited Monthly plan at any time. You'll continue to have access until 
                the end of your billing period, with no penalties or cancellation fees.
              </p>
            </Card>

            <Card className="p-6">
              <h3 className="font-semibold text-lg text-slate-900 mb-2">
                How do I get access right now?
              </h3>
              <p className="text-slate-600">
                The platform is currently invite-only. Request an invite code through the homepage, and you'll 
                get free access to test the platform. Payment options shown above are for future reference.
              </p>
            </Card>

            <Card className="p-6">
              <h3 className="font-semibold text-lg text-slate-900 mb-2">
                When will paid plans be available?
              </h3>
              <p className="text-slate-600">
                We're currently in beta testing phase. Paid plans will be available once we complete beta testing 
                and gather user feedback. Early invite code users will receive special pricing when we launch.
              </p>
            </Card>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-20 text-center">
          <div className="bg-gradient-to-r from-indigo-600 to-cyan-600 rounded-2xl p-12 text-white">
            <h2 className="text-3xl font-bold mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-indigo-100 text-lg mb-6 max-w-2xl mx-auto">
              Try our interactive demo to see how IntentAI works, or request an invite code for full platform access.
            </p>
            <div className="flex gap-4 justify-center">
              <Button
                onClick={() => navigate('/demo/excellent')}
                size="lg"
                className="bg-white text-indigo-600 hover:bg-indigo-50 px-8"
              >
                Try Demo
              </Button>
              <Button
                onClick={() => navigate('/')}
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10"
              >
                Request Invite Code
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
